import { getMomentTz } from "@dexteel/mesf-core";
import type { ExcelRow, ExcelStyle } from "ag-grid-community";
import { UTILITY_KIND, type UtilityKind } from "../../models/Utility";
import {
	UTILITY_CONSUMPTION_REPORT_MODE,
	type UtilityConsumptionPeriod,
	type UtilityConsumptionReportMode,
	type UtilityConsumptionReportRange,
} from "../models/UtilityConsumption";

const moment = getMomentTz();

export const UTILITY_EXPORT_LABEL: Record<UtilityKind, string> = {
	[UTILITY_KIND.WATER]: "Water",
	[UTILITY_KIND.GAS]: "Furnace gas",
	[UTILITY_KIND.ELECTRICITY]: "Electricity",
};

export const REPORT_MODE_EXPORT_LABEL: Record<
	UtilityConsumptionReportMode,
	string
> = {
	day: "One day, hour by hour",
	week: "One week, day by day",
	month: "One month, day by day",
	year: "One year, month by month",
	period: "Date range, day by day",
	section: "Rolling runs, one per run",
};

export const EXCEL_VALUE_STYLE = "excelValue";
export const EXCEL_RATIO_STYLE = "excelRatio";
export const EXCEL_COST_STYLE = "excelCost";
export const EXCEL_PARTIAL_STYLE = "partialCoverage";
export const EXCEL_TOTAL_STYLE = "excelTotal";

export const consumptionReportExcelStyles: ExcelStyle[] = [
	{
		id: "header",
		font: { bold: true, color: "#1F2933" },
		interior: { color: "#EDF1F5", pattern: "Solid" },
		alignment: { horizontal: "Center", vertical: "Center", wrapText: true },
		borders: {
			borderBottom: { color: "#B7C0C9", lineStyle: "Continuous", weight: 1 },
		},
	},
	{
		id: "headerGroup",
		font: { bold: true, color: "#1F2933" },
		interior: { color: "#DDE4EB", pattern: "Solid" },
		alignment: { horizontal: "Center", vertical: "Center", wrapText: true },
	},
	{
		id: "excelReportTitle",
		font: { bold: true, size: 14, color: "#1F2933" },
	},
	{
		id: "excelReportLabel",
		font: { bold: true, color: "#5B6670" },
	},
	{
		id: "excelReportNote",
		font: { color: "#5B6670", italic: true },
		alignment: { wrapText: false },
	},
	{
		id: EXCEL_VALUE_STYLE,
		numberFormat: { format: "#,##0.0" },
	},
	{
		id: EXCEL_RATIO_STYLE,
		numberFormat: { format: "#,##0.00" },
	},
	{
		id: EXCEL_COST_STYLE,
		numberFormat: { format: '"$"#,##0.00' },
	},
	{
		id: EXCEL_PARTIAL_STYLE,
		interior: { color: "#FFF4E0", pattern: "Solid" },
		font: { color: "#8A5200" },
	},
	{
		id: EXCEL_TOTAL_STYLE,
		font: { bold: true },
		borders: {
			borderTop: { color: "#8A939C", lineStyle: "Continuous", weight: 1 },
		},
	},
];

const textCell = (value: string, styleId?: string) => ({
	data: { value, type: "String" as const },
	...(styleId === undefined ? {} : { styleId }),
});

const slugify = (value: string) =>
	value
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

const compactDateKey = (dateKey: string) => dateKey.replaceAll("-", "");

interface ConsumptionReportExportRangeInput {
	mode: UtilityConsumptionReportMode;
	isSection: boolean;
	range: UtilityConsumptionReportRange;
	sectionRange: UtilityConsumptionReportRange;
	period: UtilityConsumptionPeriod | null;
}

export const getConsumptionReportExportRangeKey = ({
	mode,
	isSection,
	range,
	sectionRange,
	period,
}: ConsumptionReportExportRangeInput): string => {
	if (isSection) {
		return `runs_${compactDateKey(sectionRange.startDateKey)}_${compactDateKey(
			sectionRange.endDateKey,
		)}`;
	}
	if (mode === UTILITY_CONSUMPTION_REPORT_MODE.PERIOD) {
		return `${compactDateKey(range.startDateKey)}_${compactDateKey(
			range.endDateKey,
		)}`;
	}
	if (period === null) return "";
	if (mode === UTILITY_CONSUMPTION_REPORT_MODE.YEAR) {
		return moment(period.startUtc).format("YYYY");
	}
	return `${moment(period.startUtc).format("YYYYMMDD")}_${moment(period.endUtc)
		.subtract(1, "day")
		.format("YYYYMMDD")}`;
};

export const getConsumptionReportExportFileName = ({
	utility,
	areaName,
	rangeKey,
}: {
	utility: UtilityKind;
	areaName: string | null;
	rangeKey: string;
}): string =>
	`${[
		"utility_report",
		utility,
		areaName === null ? "" : slugify(areaName),
		rangeKey,
	]
		.filter((part) => part !== "")
		.join("_")}.xlsx`;

export const getConsumptionReportExportPeriodLabel = ({
	calendarLabel,
	isSection,
	activeRange,
}: {
	calendarLabel: string | null;
	isSection: boolean;
	activeRange: UtilityConsumptionReportRange;
}): string => {
	if (calendarLabel !== null) return calendarLabel;

	const format = (dateKey: string) =>
		moment(dateKey, "YYYY-MM-DD").format("MM/DD/YYYY");
	const range = `${format(activeRange.startDateKey)} - ${format(activeRange.endDateKey)}`;
	return isSection ? `${range}, runs finished inside it` : range;
};

const EXPORT_COLUMN_WIDTH = {
	timestamp: 108,
	cost: 92,
	figure: 84,
} as const;

export const getConsumptionReportExportColumnWidth = (
	colId: string,
): number => {
	if (colId === "eventStart" || colId === "eventEnd") {
		return EXPORT_COLUMN_WIDTH.timestamp;
	}
	if (colId === "cost" || colId === "costPerTon") {
		return EXPORT_COLUMN_WIDTH.cost;
	}
	return EXPORT_COLUMN_WIDTH.figure;
};

export interface ConsumptionReportExportContent {
	title: string;
	lines: [string, string][];
	notes: string[];
}

export const buildConsumptionReportExportHeader = ({
	title,
	lines,
}: ConsumptionReportExportContent): ExcelRow[] => [
	{ cells: [{ ...textCell(title, "excelReportTitle"), mergeAcross: 4 }] },
	...lines.map(([label, value]) => ({
		cells: [
			textCell(label, "excelReportLabel"),
			{ ...textCell(value), mergeAcross: 3 },
		],
	})),
	{ cells: [] },
];

export const buildConsumptionReportExportFooter = ({
	notes,
}: Pick<ConsumptionReportExportContent, "notes">): ExcelRow[] | undefined =>
	notes.length === 0
		? undefined
		: [
				{ cells: [] },
				...notes.map((note) => ({
					cells: [{ ...textCell(note, "excelReportNote"), mergeAcross: 4 }],
				})),
			];
